import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, Send, Trash2, Loader2, Flag, Reply, X, AtSign } from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Highlights @Full Name / @Firstname mentions of known team members in blue.
function MentionText({ text, mentionNames }) {
  if (!mentionNames.length) return <>{text}</>;
  const re = new RegExp(`(@(?:${mentionNames.map(escapeRe).join('|')}))`, 'gi');
  const parts = text.split(re);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1
          ? <span key={i} className="text-blue-400 font-medium bg-blue-500/10 rounded px-0.5">{p}</span>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </>
  );
}

export function DealComments({ projectId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [flagging, setFlagging] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [team, setTeam] = useState([]);
  const textareaRef = useRef(null);
  const { user, isAdmin } = useAuth();

  const load = async () => {
    try { const res = await api.getComments(projectId); setComments(res.data); }
    catch { /* polling: non-critical */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [projectId]);
  useEffect(() => {
    api.listTeamMembers().then(r => setTeam(r.data || [])).catch(() => {});
  }, []);

  // Known names (full + first), longest first so "@Max Mustermann" wins over "@Max".
  const mentionNames = useMemo(() => {
    const all = [];
    team.forEach(u => {
      const n = (u.name || '').trim();
      if (!n) return;
      all.push(n);
      const first = n.split(' ')[0];
      if (first && first !== n) all.push(first);
    });
    return [...new Set(all)].sort((a, b) => b.length - a.length);
  }, [team]);

  // @-mention autocomplete: active when the text ends with "@partialword"
  const mentionQuery = useMemo(() => {
    const m = text.match(/@([\wÀ-ÿ]*)$/);
    return m ? m[1].toLowerCase() : null;
  }, [text]);

  const suggestions = useMemo(() => {
    if (mentionQuery === null) return [];
    return team
      .filter(u => (u.name || '').toLowerCase().startsWith(mentionQuery))
      .slice(0, 5);
  }, [mentionQuery, team]);

  const insertMention = (name) => {
    setText(t => t.replace(/@[\wÀ-ÿ]*$/, `@${name} `));
    textareaRef.current?.focus();
  };

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.addComment(projectId, text.trim());
      setText('');
      setReplyingTo(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSending(false);
  };

  const handleDelete = async (commentId) => {
    try { await api.deleteComment(projectId, commentId); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleFlag = async (commentId, currentlyFlagged) => {
    setFlagging(commentId);
    try {
      await api.flagComment(projectId, commentId, !currentlyFlagged);
      if (!currentlyFlagged) toast.success('Flagged for team meeting');
      else toast.success('Flag removed');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setFlagging(null);
  };

  const handleReply = (comment) => {
    setReplyingTo(comment);
    setText(`@${comment.user_name} `);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (suggestions.length > 0) insertMention(suggestions[0].name);
      else handleSend();
    }
    if (e.key === 'Tab' && suggestions.length > 0) {
      e.preventDefault();
      insertMention(suggestions[0].name);
    }
    if (e.key === 'Escape' && suggestions.length > 0) {
      e.preventDefault();
      setText(t => t.replace(/@[\wÀ-ÿ]*$/, ''));
    }
  };

  return (
    <CollapsibleSection
      storageKey={`deal.${projectId}.comments`}
      icon={MessageCircle}
      iconClass="text-foreground/60"
      title="Discussion"
      count={comments.length > 0 ? comments.length : undefined}
      testId="deal-comments"
      bodyClass="p-4"
    >

      {/* Comments list */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto mb-3">
        {loading ? (
          <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
        ) : comments.length === 0 ? (
          <p className="text-xs text-muted-foreground/50 text-center py-4">No comments yet. Start the conversation.</p>
        ) : (
          comments.map(c => {
            const isOwn = c.user_id === user?.id;
            const canDelete = isOwn || isAdmin;
            return (
              <div key={c.id} className="group flex gap-2.5" data-testid={`comment-${c.id}`}>
                <div className="w-7 h-7 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-[10px] font-bold font-mono shrink-0 mt-0.5">
                  {c.user_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-foreground">{c.user_name}</span>
                    <span className="text-[9px] text-muted-foreground/50 font-mono">
                      {new Date(c.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {canDelete && (
                      <button onClick={() => handleDelete(c.id)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground/30 hover:text-red-400 transition-all">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                    <button onClick={() => handleFlag(c.id, c.flagged)}
                      disabled={flagging === c.id}
                      title={c.flagged ? 'Remove flag (unflag from meeting)' : 'Flag for team meeting'}
                      data-testid={`flag-comment-${c.id}`}
                      className={`opacity-0 group-hover:opacity-100 transition-all ${c.flagged ? 'text-amber-400 opacity-100' : 'text-muted-foreground/30 hover:text-amber-400'}`}>
                      {flagging === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" fill={c.flagged ? 'currentColor' : 'none'} />}
                    </button>
                    <button onClick={() => handleReply(c)}
                      title="Reply"
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground/30 hover:text-blue-400 transition-all">
                      <Reply className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line mt-0.5">
                    <MentionText text={c.text} mentionNames={mentionNames} />
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input */}
      {replyingTo && (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-blue-950/20 border border-blue-800/20 rounded-sm mb-1">
          <Reply className="w-3 h-3 text-blue-400 shrink-0" />
          <span className="text-[10px] text-blue-400 truncate">Replying to {replyingTo.user_name}</span>
          <button onClick={() => { setReplyingTo(null); setText(''); }} className="text-muted-foreground hover:text-foreground ml-auto shrink-0">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <div className="relative">
        {suggestions.length > 0 && (
          <div className="absolute bottom-full left-0 mb-1 w-56 bg-card border border-border rounded-sm shadow-lg z-20 overflow-hidden" data-testid="mention-suggestions">
            {suggestions.map((u, i) => (
              <button key={u.id} onClick={() => insertMention(u.name)}
                data-testid={`mention-suggestion-${i}`}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-secondary transition-colors ${i === 0 ? 'bg-secondary/60' : ''}`}>
                <div className="w-5 h-5 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-[9px] font-bold font-mono shrink-0">
                  {u.name?.charAt(0)?.toUpperCase()}
                </div>
                <span className="text-xs text-foreground truncate">{u.name}</span>
                {i === 0 && <span className="ml-auto text-[9px] text-muted-foreground/50 font-mono">↵ / Tab</span>}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2 border-t border-border pt-3">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write a comment... use @name to notify a teammate"
            rows={1}
            data-testid="comment-input"
            className="flex-1 bg-secondary border border-border rounded-sm text-sm text-foreground p-2.5 outline-none resize-none leading-relaxed min-h-[36px] max-h-[120px]"
            style={{ height: Math.min(120, Math.max(36, text.split('\n').length * 22)) }}
          />
          <Button onClick={handleSend} disabled={!text.trim() || sending}
            data-testid="comment-send-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm h-9 px-3">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      <p className="text-[9px] text-muted-foreground/50 mt-1.5 flex items-center gap-1">
        <AtSign className="w-2.5 h-2.5" /> Mention a teammate with @ — they get a push notification.
      </p>
    </CollapsibleSection>
  );
}
